import * as React from 'react';
import { Button } from '@/components/ui/button';
import { CloudUpload, Loader } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';
import { RouteParams } from '../library/types';
import { uploadFiles } from './uploadSlice';
import { RootState } from '../../reducers';
import ALLOWED_TYPES from './AllowedTypes';
import useLimits from '../plan/hooks/limits';
import validateFiles, { UploadValidationError } from './fileValidation';
import logger from '../../lib/logger';
import { setAlertMessage } from '../dashboard/dashboardSlice';
import { trackMediaAction } from '../library/actions/mediaActions/mediaActionsSlice';
import { useBooleanFeature } from '../../hooks/useFeatures';

const VisuallyHiddenInput = React.forwardRef<
  HTMLInputElement,
  React.InputHTMLAttributes<HTMLInputElement>
>((props, ref) => (
  <input
    ref={ref}
    {...props}
    style={{
      clip: 'rect(0 0 0 0)',
      clipPath: 'inset(50%)',
      height: 1,
      overflow: 'hidden',
      position: 'absolute',
      bottom: 0,
      left: 0,
      whiteSpace: 'nowrap',
      width: 1,
    }}
  />
));
VisuallyHiddenInput.displayName = 'VisuallyHiddenInput';

export default function InputFileUpload() {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { upload } = useSelector((state: RootState) => state);
  const { status } = upload;
  const { teamId, folderId } = useParams<RouteParams>();
  const guardLimits = useLimits();
  const { getFeature } = useBooleanFeature();
  const isUploadUnguarded = getFeature('feature_unguard_upload');
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [searchParams] = useSearchParams();
  const [isValidating, setIsValidating] = React.useState(false);

  // Conditional guard function that respects the feature flag
  const conditionalGuard = React.useCallback(
    (limitType: string, callback: () => void) => {
      if (isUploadUnguarded) {
        // If upload is unguarded, execute callback directly without limit checks
        callback();
      } else {
        // Use normal guard limits
        guardLimits(limitType as any, callback);
      }
    },
    [isUploadUnguarded, guardLimits],
  );

  const getFileExtension = (mimeType: string): string => {
    const extensions: { [key: string]: string } = {
      'video/mp4': '.mp4',
      'video/quicktime': '.mov',
      'video/webm': '.webm',
      'video/x-msvideo': '.avi',
      'image/jpeg': '.jpg',
      'image/png': '.png',
      'image/gif': '.gif',
    };
    return extensions[mimeType] || '';
  };

  // Handle shared files
  React.useEffect(() => {
    const handleSharedFile = async () => {
      if (searchParams.get('share') === 'true' && teamId && folderId) {
        try {
          const cache = await caches.open('shared-files');
          const response = await cache.match('shared-file');

          if (response) {
            const file = await response.blob();
            const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
            const fileName = `shared-file-${timestamp}${getFileExtension(
              file.type,
            )}`;
            const filesArray = [
              new File([file], fileName, { type: file.type }),
            ];

            const validationResult = await validateFiles(filesArray);
            if (!validationResult.isValid) {
              const { errorMessage } =
                validationResult as UploadValidationError;
              logger.error(new Error(errorMessage));
              dispatch(
                setAlertMessage({
                  message: errorMessage || t('upload.errors.sharedFileInvalid'),
                  type: 'error',
                }),
              );
              return;
            }

            dispatch(uploadFiles({ teamId, folderId, files: filesArray }));
            // Clean up the cached file
            await cache.delete('shared-file');
          }
        } catch (error) {
          logger.error(error);
          dispatch(
            setAlertMessage({
              message: t('upload.errors.failedToProcess'),
              type: 'error',
            }),
          );
        }
      }
    };

    handleSharedFile();
  }, [searchParams, teamId, folderId, dispatch, t]);

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { files } = event.target;
    if (teamId && folderId && files) {
      const filesArray = Array.from(files);
      setIsValidating(true);
      const validationResult = await validateFiles(filesArray);

      if (!validationResult.isValid) {
        const { errorMessage } = validationResult as UploadValidationError;
        logger.error(new Error(errorMessage));
        dispatch(
          setAlertMessage({
            message: errorMessage || t('upload.errors.invalidFiles'),
            type: 'error',
          }),
        );
        setIsValidating(false);
        return;
      }
      if (filesArray.length > 1) {
        conditionalGuard('bulkUpload', () => {
          dispatch(uploadFiles({ teamId, folderId, files: filesArray }));
          setIsValidating(false);
        });
      } else {
        dispatch(uploadFiles({ teamId, folderId, files: filesArray }));
        setIsValidating(false);
      }
    }
  };

  // Reset validating state when upload completes/fails
  React.useEffect(() => {
    if (status === 'succeeded' || status === 'failed') {
      setIsValidating(false);
    }
  }, [status]);

  const handleButtonClick = (e: React.MouseEvent) => {
    e.preventDefault();
    dispatch(
      trackMediaAction({
        actionKey: 'upload',
        actionLabel: 'Upload File',
        teamId: teamId?.toString(),
        folderId: folderId?.toString(),
      }),
    );
    conditionalGuard('uploadButton', () => {
      fileInputRef.current?.click();
    });
  };

  const isLoading = status === 'pending' || isValidating;

  return (
    <div className="flex flex-col items-center min-w-[130px] flex-shrink-0">
      <Button
        onClick={handleButtonClick}
        disabled={isLoading}
        variant="ghost"
        className="w-[120px] h-[75px] rounded-lg bg-white dark:bg-secondary border border-gray-100 dark:border-border text-gray-700 dark:text-gray-200 hover:bg-blue-500 hover:text-white hover:border-transparent transition-all duration-[400ms] ease-in-out transform hover:scale-[1.02] flex flex-col items-center justify-center gap-1.5 px-3 disabled:opacity-50 disabled:hover:scale-100 disabled:hover:bg-white dark:disabled:hover:bg-gray-800 disabled:hover:text-gray-700 dark:disabled:hover:text-gray-200 group"
      >
        {isLoading ? (
          <Loader
            size={24}
            className="w-6 h-6 animate-spin"
            style={{ width: '24px', height: '24px' }}
          />
        ) : (
          <CloudUpload
            size={24}
            className="w-6 h-6 transition-transform duration-[400ms] ease-in-out group-hover:scale-110"
            style={{ width: '24px', height: '24px' }}
          />
        )}
        <span className="text-xs font-medium whitespace-nowrap transition-all duration-[400ms] ease-in-out">
          {isLoading ? t('upload.uploading') : t('upload.file')}
        </span>
      </Button>
      <VisuallyHiddenInput
        ref={fileInputRef}
        type="file"
        multiple
        accept={ALLOWED_TYPES.join(',')}
        onChange={handleFileChange}
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );
}
